import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  InfoCircleOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { Spin, Tooltip } from 'antd';
import { useGetRegionsQuery } from 'gql/types';
import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useOperationsDashboardContext } from '../../contexts/OperationsDashboardContext';
import { getDateRangeBounds } from '../../contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import {
  GET_OPS_OVERVIEW_DB_SUMMARY_QUERY,
  GET_OPS_OVERVIEW_WOO_SUMMARY_QUERY,
} from '../../graphql/queries';
import { buildOpsDrilldownUrl } from '../../utils/buildOpsDrilldownUrl';
import styles from '../OperationsDashboard/ops-theme.module.css';
import OpsMetricDetailModal, {
  IOpsMetricDetailConfig,
} from '../OpsMetricDetailModal';
import OpsPeriodFilter from '../OpsPeriodFilter';

/** Trend arrow matching ops dashboard style */
const TrendIndicator: FC<{
  current: number;
  previous: number | undefined;
  lowerIsBetter?: boolean;
}> = ({ current, previous, lowerIsBetter }) => {
  if (previous === undefined) return null;
  const delta = current - previous;
  if (previous === 0 && delta === 0) return null;
  if (previous === 0) return null;
  const pct = Math.abs((delta / previous) * 100);
  const isFlat = pct < 1;
  const up = delta > 0;

  if (isFlat) {
    return (
      <span
        className={styles.statusWarn}
        style={{ fontSize: 12, marginLeft: 6 }}
      >
        <ArrowUpOutlined
          style={{ transform: 'rotate(90deg)', marginRight: 2 }}
        />
        0%
      </span>
    );
  }

  const display = pct < 1 ? pct.toFixed(1) : `${Math.round(pct)}`;
  const isGood = lowerIsBetter ? !up : up;
  return (
    <span
      className={isGood ? styles.statusUp : styles.statusDown}
      style={{ fontSize: 12, marginLeft: 6 }}
    >
      {up ? (
        <ArrowUpOutlined style={{ marginRight: 2 }} />
      ) : (
        <ArrowDownOutlined style={{ marginRight: 2 }} />
      )}
      {display}%
    </span>
  );
};

/** Inline breakdown row for product class data */
const BreakdownRow: FC<{
  label: string;
  value: string;
  indent?: boolean;
  current?: number;
  previous?: number;
  lowerIsBetter?: boolean;
}> = ({ label, value, indent, current, previous, lowerIsBetter }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: indent ? '1px 0 1px 12px' : '2px 0',
    }}
  >
    <span
      className={styles.summaryCardSub}
      style={{
        letterSpacing: '0.05em',
        fontSize: indent ? 10 : 13,
        fontWeight: indent ? 400 : 600,
        color: indent ? 'var(--text-muted)' : 'var(--text-secondary)',
      }}
    >
      {label}
    </span>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontWeight: indent ? 500 : 700,
          fontSize: indent ? 10 : 14,
          color: indent ? 'var(--text-muted)' : 'var(--text-primary)',
        }}
      >
        {value}
      </span>
      {current !== undefined && previous !== undefined && (
        <TrendIndicator
          current={current}
          previous={previous}
          lowerIsBetter={lowerIsBetter}
        />
      )}
    </span>
  </div>
);

/** Describe a trend as text */
function trendText(
  current: number,
  previous: number | undefined,
  unit: string,
  lowerIsBetter?: boolean
): string {
  if (previous === undefined) return '';
  if (previous === 0) return 'No prior period data';
  const delta = current - previous;
  const pct = Math.abs((delta / previous) * 100);
  const dir = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  if (pct < 1) return 'Flat vs prior period';
  const pctDisplay = pct < 1 ? pct.toFixed(1) : `${Math.round(pct)}`;
  const prevDisplay =
    unit === '$'
      ? `$${previous.toLocaleString()}`
      : `${previous.toLocaleString()} ${unit}`;
  return `${
    dir === 'up' ? '↑' : '↓'
  } ${pctDisplay}% vs prior period (${prevDisplay})`;
}

/** Map date range type to a period label for the units shipped card */
function getPeriodLabel(dateRange: string): string {
  if (dateRange.includes('day') || dateRange.includes('yesterday'))
    return 'DAILY';
  if (dateRange.includes('week') || dateRange.includes('Week')) return 'WEEKLY';
  if (dateRange.includes('quarter') || dateRange.includes('Quarter'))
    return 'QUARTERLY';
  if (dateRange.includes('year') || dateRange.includes('Year')) return 'YEARLY';
  if (dateRange === 'custom') return 'PERIOD';
  return 'MONTHLY';
}

const OpsOverviewTab: FC = () => {
  const navigate = useNavigate();
  const {
    overviewFilters,
    setOverviewDateRange,
    setOverviewRegionId,
    isDarkMode,
  } = useOperationsDashboardContext();

  const [modalConfig, setModalConfig] = useState<IOpsMetricDetailConfig | null>(
    null
  );
  const openModal = (config: IOpsMetricDetailConfig, e: React.MouseEvent) => {
    e.stopPropagation();
    setModalConfig(config);
  };

  const regionsQuery = useGetRegionsQuery();
  const regions = useMemo(
    () =>
      regionsQuery.data?.getRegions?.map((r) => ({
        id: r.id,
        name: r.name,
      })) ?? [],
    [regionsQuery.data]
  );

  const { dateBounds, prevStart, prevEnd } = useMemo(() => {
    const current = getDateRangeBounds(
      overviewFilters.dateRange,
      overviewFilters.customDateRange
    );
    const durationMs = current.end.getTime() - current.start.getTime();
    const pEnd = new Date(current.start.getTime() - 1);
    const pStart = new Date(pEnd.getTime() - durationMs);
    return { dateBounds: current, prevStart: pStart, prevEnd: pEnd };
  }, [overviewFilters.dateRange, overviewFilters.customDateRange]);

  const { opsSettings } = useOperationsDashboardContext();

  const selectedRegionName = useMemo(
    () =>
      overviewFilters.regionId
        ? regions.find((r) => r.id === overviewFilters.regionId)?.name
        : undefined,
    [overviewFilters.regionId, regions]
  );

  // Resolve WC location names for the selected region
  const wcLocationNames = useMemo(() => {
    if (!overviewFilters.regionId) return undefined;
    const mapping = opsSettings.wcRegionLocationMap?.[overviewFilters.regionId];
    if (!mapping) return undefined;
    const names = mapping
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return names.length > 0 ? names : undefined;
  }, [overviewFilters.regionId, opsSettings.wcRegionLocationMap]);

  const sharedInput = useMemo(
    () => ({
      start: dateBounds.start.toISOString(),
      end: dateBounds.end.toISOString(),
      prevStart: prevStart.toISOString(),
      prevEnd: prevEnd.toISOString(),
      regionIds: overviewFilters.regionId
        ? [overviewFilters.regionId]
        : undefined,
      wcPrbcProductIds: opsSettings.wcPrbcProductIds,
      wcFfpSglProductIds: opsSettings.wcFfpSglProductIds,
      wcFfpDblProductIds: opsSettings.wcFfpDblProductIds,
      wcLocationNames,
    }),
    [
      dateBounds,
      prevStart,
      prevEnd,
      overviewFilters.regionId,
      opsSettings.wcPrbcProductIds,
      opsSettings.wcFfpSglProductIds,
      opsSettings.wcFfpDblProductIds,
      wcLocationNames,
    ]
  );

  // DB metrics — fast (units shipped, wastage, turnover)
  const { data: dbData, loading: dbLoading } = useQuery(
    GET_OPS_OVERVIEW_DB_SUMMARY_QUERY,
    {
      variables: { input: sharedInput },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }
  );

  // WooCommerce metrics — slower (external API, cached on backend)
  const { data: wooData, loading: wooLoading } = useQuery(
    GET_OPS_OVERVIEW_WOO_SUMMARY_QUERY,
    {
      variables: { input: sharedInput },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }
  );

  const dbStats = dbData?.getOpsOverviewDbSummary;
  const wooStats = wooData?.getOpsOverviewWooSummary;
  // Merged view for backward compat with card rendering
  const stats = useMemo(
    () => (dbStats || wooStats ? { ...dbStats, ...wooStats } : undefined),
    [dbStats, wooStats]
  );
  const loading = dbLoading && wooLoading; // true only if BOTH are still loading

  const handleDrilldown = (metricKey: string) => {
    const url = buildOpsDrilldownUrl({
      dataTable: metricKey,
      dateRange: overviewFilters.dateRange,
      customDateRange: overviewFilters.customDateRange,
      regionId: overviewFilters.regionId,
    });
    navigate(url);
  };

  return (
    <div className={styles.animFadeUp}>
      <OpsPeriodFilter
        value={overviewFilters.dateRange}
        customRange={overviewFilters.customDateRange}
        onChange={setOverviewDateRange}
        regions={regions}
        regionId={overviewFilters.regionId}
        onRegionChange={setOverviewRegionId}
      />

      <div style={{ marginBottom: 12 }}>
        <span className={styles.summaryCardLabel} style={{ fontSize: 12 }}>
          INVENTORY &amp; SALES TRENDS
        </span>
      </div>

      <div className={styles.summaryCardsRow}>
        {/* ── Monthly Units Shipped ── */}
        <div className={styles.summaryCard}>
          <div
            className={styles.summaryCardInfoTab}
            onClick={(e) => {
              const s = stats;
              const bd = s?.unitsShippedBreakdown;
              const prevBd = s?.prevUnitsShippedBreakdown;
              openModal(
                {
                  title: `${getPeriodLabel(
                    overviewFilters.dateRange
                  )} Units Shipped`,
                  description: (
                    <>
                      Total product units with <strong>Sold</strong> status
                      during the selected period, based on the product&apos;s
                      sold date (updatedAt). Broken down by product class using
                      the WC Product ID Mapping in settings.
                    </>
                  ),
                  rows: [
                    {
                      label: 'Total Shipped',
                      value: s?.monthlyUnitsShipped?.toLocaleString() ?? '—',
                      bold: true,
                      color: '#4B9EFF',
                    },
                    {
                      label: 'Trend',
                      value: trendText(
                        s?.monthlyUnitsShipped ?? 0,
                        s?.prevMonthlyUnitsShipped,
                        'units'
                      ),
                    },
                    {
                      label: 'pRBC',
                      value: bd?.prbcTotal?.toLocaleString() ?? '0',
                      bold: true,
                    },
                    {
                      label: 'pRBC Trend',
                      value: trendText(
                        bd?.prbcTotal ?? 0,
                        prevBd?.prbcTotal,
                        'units'
                      ),
                      indent: true,
                    },
                    {
                      label: 'FFP',
                      value: bd?.ffpTotal?.toLocaleString() ?? '0',
                      bold: true,
                    },
                    {
                      label: 'FFP Trend',
                      value: trendText(
                        bd?.ffpTotal ?? 0,
                        prevBd?.ffpTotal,
                        'units'
                      ),
                      indent: true,
                    },
                  ],
                  formula:
                    'COUNT(products WHERE status = Sold AND updatedAt IN period)',
                },
                e
              );
            }}
          >
            <InfoCircleOutlined />
          </div>
          <div className={styles.summaryCardLabel}>
            {getPeriodLabel(overviewFilters.dateRange)} UNITS SHIPPED
          </div>
          <div className={styles.healthFlagCardValueRow}>
            {dbLoading ? (
              <Spin size="small" />
            ) : (
              <>
                <span className={styles.summaryCardValue}>
                  {stats?.monthlyUnitsShipped?.toLocaleString() ?? '—'}
                </span>
                <TrendIndicator
                  current={stats?.monthlyUnitsShipped ?? 0}
                  previous={stats?.prevMonthlyUnitsShipped}
                />
              </>
            )}
            <Tooltip title="View details in Drilldown tab">
              <button
                className={styles.healthFlagDrilldownBtn}
                onClick={() => handleDrilldown('overview:unitsShipped')}
              >
                <TableOutlined />
              </button>
            </Tooltip>
          </div>
          <Tooltip title="Total product units shipped during the selected period, across all locations and product types.">
            <div className={styles.summaryCardSub} style={{ cursor: 'help' }}>
              units
            </div>
          </Tooltip>
          {!dbLoading && stats?.unitsShippedBreakdown && (
            <div
              style={{
                marginTop: 8,
                borderTop: '1px solid var(--border-subtle)',
                paddingTop: 6,
              }}
            >
              <BreakdownRow
                label="pRBC"
                value={stats.unitsShippedBreakdown.prbcTotal.toLocaleString()}
                current={stats.unitsShippedBreakdown.prbcTotal}
                previous={stats.prevUnitsShippedBreakdown?.prbcTotal}
              />
              <BreakdownRow
                label="FFP"
                value={stats.unitsShippedBreakdown.ffpTotal.toLocaleString()}
                current={stats.unitsShippedBreakdown.ffpTotal}
                previous={stats.prevUnitsShippedBreakdown?.ffpTotal}
              />
              <BreakdownRow
                label="SGL"
                value={stats.unitsShippedBreakdown.ffpSgl.toLocaleString()}
                indent
              />
              <BreakdownRow
                label="DBL"
                value={stats.unitsShippedBreakdown.ffpDbl.toLocaleString()}
                indent
              />
            </div>
          )}
        </div>

        {/* ── Wastage / Discard Rate ── */}
        <div className={styles.summaryCard}>
          <div
            className={styles.summaryCardInfoTab}
            onClick={(e) => {
              const s = stats;
              const wb = s?.wastageBreakdown;
              openModal(
                {
                  title: 'Wastage / Discard Rate',
                  description: (
                    <>
                      Percentage of units <strong>Discarded</strong> relative to
                      total units produced (all statuses) during the period,
                      based on product createdAt. Lower is better.
                    </>
                  ),
                  rows: [
                    {
                      label: 'Overall Rate',
                      value: `${s?.wastageRate?.toFixed(1) ?? '—'}%`,
                      bold: true,
                      color: '#E8008A',
                    },
                    {
                      label: 'Total Produced',
                      value: (dbStats?.totalProduced ?? 0).toLocaleString(),
                    },
                    {
                      label: 'Total Discarded',
                      value: (dbStats?.totalDiscarded ?? 0).toLocaleString(),
                    },
                    {
                      label: 'Trend',
                      value: trendText(
                        s?.wastageRate ?? 0,
                        s?.prevWastageRate,
                        '%',
                        true
                      ),
                    },
                    {
                      label: 'pRBC Rate',
                      value: `${wb?.prbcRate?.toFixed(1) ?? '0'}%`,
                      bold: true,
                    },
                    {
                      label: 'pRBC Produced / Discarded',
                      value: `${(
                        dbStats?.prbcProduced ?? 0
                      ).toLocaleString()} / ${(
                        dbStats?.prbcDiscarded ?? 0
                      ).toLocaleString()}`,
                      indent: true,
                    },
                    {
                      label: 'pRBC Trend',
                      value: trendText(
                        wb?.prbcRate ?? 0,
                        s?.prevPrbcWastageRate,
                        '%',
                        true
                      ),
                      indent: true,
                    },
                    {
                      label: 'FFP Rate',
                      value: `${wb?.ffpRate?.toFixed(1) ?? '0'}%`,
                      bold: true,
                    },
                    {
                      label: 'FFP Produced / Discarded',
                      value: `${(
                        dbStats?.ffpProduced ?? 0
                      ).toLocaleString()} / ${(
                        dbStats?.ffpDiscarded ?? 0
                      ).toLocaleString()}`,
                      indent: true,
                    },
                    {
                      label: 'FFP Trend',
                      value: trendText(
                        wb?.ffpRate ?? 0,
                        s?.prevFfpWastageRate,
                        '%',
                        true
                      ),
                      indent: true,
                    },
                    ...(dbStats?.topDiscardReasons?.length
                      ? [
                          {
                            label: '── Top Discard Reasons ──',
                            value: '',
                            bold: true,
                          },
                          ...dbStats.topDiscardReasons.map(
                            (r: { reason: string; count: number }) => {
                              const pct =
                                (dbStats?.totalDiscarded ?? 0) > 0
                                  ? (
                                      (r.count /
                                        (dbStats?.totalDiscarded ?? 1)) *
                                      100
                                    ).toFixed(1)
                                  : '0.0';
                              return {
                                label: r.reason,
                                value: `${r.count} (${pct}%)`,
                                indent: true,
                              };
                            }
                          ),
                        ]
                      : []),
                  ],
                  formula:
                    '(Discarded / Total Produced) × 100 — lower is better',
                },
                e
              );
            }}
          >
            <InfoCircleOutlined />
          </div>
          <div className={styles.summaryCardLabel}>WASTAGE / DISCARD RATE</div>
          <div className={styles.healthFlagCardValueRow}>
            {dbLoading ? (
              <Spin size="small" />
            ) : (
              <>
                <span className={styles.summaryCardValue}>
                  {stats ? `${stats.wastageRate.toFixed(1)}%` : '—'}
                </span>
                <TrendIndicator
                  current={stats?.wastageRate ?? 0}
                  previous={stats?.prevWastageRate}
                  lowerIsBetter
                />
              </>
            )}
            <Tooltip title="View details in Drilldown tab">
              <button
                className={styles.healthFlagDrilldownBtn}
                onClick={() => handleDrilldown('overview:wastage')}
              >
                <TableOutlined />
              </button>
            </Tooltip>
          </div>
          <Tooltip title="Percentage of units discarded relative to total units produced, broken down by reason code and location.">
            <div className={styles.summaryCardSub} style={{ cursor: 'help' }}>
              of total units
            </div>
          </Tooltip>
          {!dbLoading && stats?.wastageBreakdown && (
            <div
              style={{
                marginTop: 8,
                borderTop: '1px solid var(--border-subtle)',
                paddingTop: 6,
              }}
            >
              <BreakdownRow
                label="pRBC"
                value={`${stats.wastageBreakdown.prbcRate.toFixed(1)}%`}
                current={stats.wastageBreakdown.prbcRate}
                previous={stats.prevPrbcWastageRate}
                lowerIsBetter
              />
              <BreakdownRow
                label="FFP"
                value={`${stats.wastageBreakdown.ffpRate.toFixed(1)}%`}
                current={stats.wastageBreakdown.ffpRate}
                previous={stats.prevFfpWastageRate}
                lowerIsBetter
              />
              <BreakdownRow
                label="SGL"
                value={`${stats.wastageBreakdown.ffpSglRate.toFixed(1)}%`}
                indent
              />
              <BreakdownRow
                label="DBL"
                value={`${stats.wastageBreakdown.ffpDblRate.toFixed(1)}%`}
                indent
              />
            </div>
          )}
        </div>

        {/* ── Inventory Turnover ── */}
        <div className={styles.summaryCard}>
          <div
            className={styles.summaryCardInfoTab}
            onClick={(e) => {
              const s = stats;
              const tb = s?.turnoverBreakdown;
              const bd = s?.unitsShippedBreakdown;
              // Compute IN (produced) from OUT / ratio
              const totalOut = s?.monthlyUnitsShipped ?? 0;
              const totalIn =
                s?.inventoryTurnover && s.inventoryTurnover > 0
                  ? Math.round(totalOut / s.inventoryTurnover)
                  : 0;
              const prbcOut = bd?.prbcTotal ?? 0;
              const prbcIn =
                tb?.prbcRate && tb.prbcRate > 0
                  ? Math.round(prbcOut / tb.prbcRate)
                  : 0;
              const ffpOut = bd?.ffpTotal ?? 0;
              const ffpIn =
                tb?.ffpRate && tb.ffpRate > 0
                  ? Math.round(ffpOut / tb.ffpRate)
                  : 0;
              openModal(
                {
                  title: 'Inventory Turnover',
                  description: (
                    <>
                      Ratio of units <strong>Sold</strong> (out) to units{' '}
                      <strong>Produced</strong> (in) during the period. Higher
                      turnover means more of what&apos;s produced gets sold. A
                      value of 1.0× means all produced units were sold.
                    </>
                  ),
                  rows: [
                    {
                      label: 'Overall Turnover',
                      value: `${s?.inventoryTurnover?.toFixed(1) ?? '—'}×`,
                      bold: true,
                      color: '#F7A500',
                    },
                    {
                      label: 'Units IN (Produced)',
                      value: totalIn.toLocaleString(),
                    },
                    {
                      label: 'Units OUT (Sold)',
                      value: totalOut.toLocaleString(),
                    },
                    {
                      label: 'Trend',
                      value: trendText(
                        s?.inventoryTurnover ?? 0,
                        s?.prevInventoryTurnover,
                        '×'
                      ),
                    },
                    {
                      label: 'pRBC Turnover',
                      value: `${tb?.prbcRate?.toFixed(1) ?? '0'}×`,
                      bold: true,
                    },
                    {
                      label: 'pRBC IN / OUT',
                      value: `${prbcIn.toLocaleString()} / ${prbcOut.toLocaleString()}`,
                      indent: true,
                    },
                    {
                      label: 'pRBC Trend',
                      value: trendText(
                        tb?.prbcRate ?? 0,
                        s?.prevPrbcTurnover,
                        '×'
                      ),
                      indent: true,
                    },
                    {
                      label: 'FFP Turnover',
                      value: `${tb?.ffpRate?.toFixed(1) ?? '0'}×`,
                      bold: true,
                    },
                    {
                      label: 'FFP IN / OUT',
                      value: `${ffpIn.toLocaleString()} / ${ffpOut.toLocaleString()}`,
                      indent: true,
                    },
                    {
                      label: 'FFP Trend',
                      value: trendText(
                        tb?.ffpRate ?? 0,
                        s?.prevFfpTurnover,
                        '×'
                      ),
                      indent: true,
                    },
                  ],
                  formula: 'Units Sold / Units Produced — higher is better',
                },
                e
              );
            }}
          >
            <InfoCircleOutlined />
          </div>
          <div className={styles.summaryCardLabel}>INVENTORY TURNOVER</div>
          <div className={styles.healthFlagCardValueRow}>
            {dbLoading ? (
              <Spin size="small" />
            ) : (
              <>
                <span className={styles.summaryCardValue}>
                  {stats ? `${stats.inventoryTurnover.toFixed(1)}×` : '—'}
                </span>
                <TrendIndicator
                  current={stats?.inventoryTurnover ?? 0}
                  previous={stats?.prevInventoryTurnover}
                />
              </>
            )}
            <Tooltip title="View details in Drilldown tab">
              <button
                className={styles.healthFlagDrilldownBtn}
                onClick={() => handleDrilldown('overview:turnover')}
              >
                <TableOutlined />
              </button>
            </Tooltip>
          </div>
          <Tooltip title="Ratio of units shipped out vs units received in, per location per period. Higher = faster turnover.">
            <div className={styles.summaryCardSub} style={{ cursor: 'help' }}>
              in / out ratio
            </div>
          </Tooltip>
          {!dbLoading && stats?.turnoverBreakdown && (
            <div
              style={{
                marginTop: 8,
                borderTop: '1px solid var(--border-subtle)',
                paddingTop: 6,
              }}
            >
              <BreakdownRow
                label="pRBC"
                value={`${stats.turnoverBreakdown.prbcRate.toFixed(1)}×`}
                current={stats.turnoverBreakdown.prbcRate}
                previous={stats.prevPrbcTurnover}
              />
              <BreakdownRow
                label="FFP"
                value={`${stats.turnoverBreakdown.ffpRate.toFixed(1)}×`}
                current={stats.turnoverBreakdown.ffpRate}
                previous={stats.prevFfpTurnover}
              />
              <BreakdownRow
                label="SGL"
                value={`${stats.turnoverBreakdown.ffpSglRate.toFixed(1)}×`}
                indent
              />
              <BreakdownRow
                label="DBL"
                value={`${stats.turnoverBreakdown.ffpDblRate.toFixed(1)}×`}
                indent
              />
            </div>
          )}
        </div>

        {/* ── Reservation Fill Rate ── */}
        <div className={styles.summaryCard}>
          <div
            className={styles.summaryCardInfoTab}
            onClick={(e) =>
              openModal(
                {
                  title: 'Reservation Fill Rate',
                  description: (
                    <>
                      Percentage of recurring reservation templates that were
                      actually fulfilled by client. Measures how reliably
                      scheduled orders are completed. This metric is not yet
                      wired to live data.
                    </>
                  ),
                  rows: [
                    {
                      label: 'Fill Rate',
                      value: `${
                        stats?.reservationFillRate?.toFixed(1) ?? '—'
                      }%`,
                      bold: true,
                      color: '#00E5A0',
                    },
                  ],
                  formula: '(Fulfilled Reservations / Templates) × 100',
                },
                e
              )
            }
          >
            <InfoCircleOutlined />
          </div>
          <div className={styles.summaryCardLabel}>RESERVATION FILL RATE</div>
          <div className={styles.healthFlagCardValueRow}>
            {dbLoading ? (
              <Spin size="small" />
            ) : (
              <span className={styles.summaryCardValue}>
                {stats ? `${stats.reservationFillRate.toFixed(1)}%` : '—'}
              </span>
            )}
            <Tooltip title="View details in Drilldown tab">
              <button
                className={styles.healthFlagDrilldownBtn}
                onClick={() => handleDrilldown('overview:fillRate')}
              >
                <TableOutlined />
              </button>
            </Tooltip>
          </div>
          <Tooltip title="Percentage of recurring reservation templates that were actually fulfilled, by client.">
            <div className={styles.summaryCardSub} style={{ cursor: 'help' }}>
              fulfilled / template
            </div>
          </Tooltip>
        </div>

        {/* ── WooCommerce Sales ── */}
        <div className={styles.summaryCard}>
          <div
            className={styles.summaryCardInfoTab}
            onClick={(e) => {
              const s = stats;
              openModal(
                {
                  title: 'WooCommerce Sales',
                  description: (
                    <>
                      Live data from WooCommerce API. Includes orders with
                      status <strong>completed</strong>,{' '}
                      <strong>completed-2</strong> (delivered), or{' '}
                      <strong>delayed</strong> created during the period.
                      Product classification uses WC Product ID Mapping from
                      settings. Region filter uses WC Region → Location Mapping.
                    </>
                  ),
                  rows: [
                    {
                      label: 'Orders',
                      value: s?.wooCommerceSalesCount?.toLocaleString() ?? '—',
                      bold: true,
                      color: '#5B2D8E',
                    },
                    {
                      label: 'Orders Trend',
                      value: trendText(
                        s?.wooCommerceSalesCount ?? 0,
                        s?.prevWooCommerceSalesCount,
                        'orders'
                      ),
                      indent: true,
                    },
                    {
                      label: 'Revenue',
                      value: `$${
                        s?.wooCommerceSalesTotal?.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }) ?? '—'
                      }`,
                      bold: true,
                    },
                    {
                      label: 'Revenue Trend',
                      value: trendText(
                        s?.wooCommerceSalesTotal ?? 0,
                        s?.prevWooCommerceSalesTotal,
                        '$'
                      ),
                      indent: true,
                    },
                    {
                      label: 'pRBC Units',
                      value: s?.wooPrbcCount?.toLocaleString() ?? '0',
                      bold: true,
                    },
                    {
                      label: 'pRBC Trend',
                      value: trendText(
                        s?.wooPrbcCount ?? 0,
                        s?.prevWooPrbcCount,
                        'units'
                      ),
                      indent: true,
                    },
                    {
                      label: 'FFP Units',
                      value: String(
                        (s?.wooFfpSglCount ?? 0) + (s?.wooFfpDblCount ?? 0)
                      ),
                      bold: true,
                    },
                    {
                      label: 'FFP Trend',
                      value: trendText(
                        (s?.wooFfpSglCount ?? 0) + (s?.wooFfpDblCount ?? 0),
                        s?.prevWooFfpCount,
                        'units'
                      ),
                      indent: true,
                    },
                  ],
                  formula:
                    'WooCommerce API: orders WHERE status IN (completed, completed-2, delayed) AND date IN period',
                },
                e
              );
            }}
          >
            <InfoCircleOutlined />
          </div>
          <div className={styles.summaryCardLabel}>WOOCOMMERCE SALES</div>
          <div className={styles.healthFlagCardValueRow}>
            {wooLoading ? (
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    height: 4,
                    borderRadius: 2,
                    background: 'var(--border-subtle, rgba(255,255,255,0.08))',
                    overflow: 'hidden',
                    marginTop: 16,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      borderRadius: 2,
                      background: 'var(--magenta, #E8008A)',
                      animation: 'wooLoadFill 8s ease-out forwards',
                    }}
                  />
                </div>
                <style>{`
                  @keyframes wooLoadFill {
                    0% { width: 0%; }
                    20% { width: 30%; }
                    50% { width: 60%; }
                    80% { width: 80%; }
                    100% { width: 92%; }
                  }
                `}</style>
              </div>
            ) : (
              <>
                <span className={styles.summaryCardValue}>
                  {stats?.wooCommerceSalesCount?.toLocaleString() ?? '—'}
                </span>
                <TrendIndicator
                  current={stats?.wooCommerceSalesCount ?? 0}
                  previous={stats?.prevWooCommerceSalesCount}
                />
              </>
            )}
            <Tooltip title="View details in Drilldown tab">
              <button
                className={styles.healthFlagDrilldownBtn}
                onClick={() => handleDrilldown('overview:sales')}
              >
                <TableOutlined />
              </button>
            </Tooltip>
          </div>
          {!wooLoading && stats ? (
            <div className={styles.summaryCardSub}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontWeight: 800,
                    color: 'var(--text-primary)',
                    fontSize: 13,
                  }}
                >
                  $
                  {stats.wooCommerceSalesTotal?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
                <TrendIndicator
                  current={stats.wooCommerceSalesTotal ?? 0}
                  previous={stats.prevWooCommerceSalesTotal}
                />{' '}
                revenue
              </div>
            </div>
          ) : (
            <div className={styles.summaryCardSub}>orders</div>
          )}
          {!wooLoading && stats && (
            <div
              style={{
                marginTop: 8,
                borderTop: '1px solid var(--border-subtle)',
                paddingTop: 6,
              }}
            >
              <div
                className={styles.summaryCardSub}
                style={{
                  fontSize: 10,
                  marginBottom: 4,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                {selectedRegionName || 'All Regions'}
              </div>
              <BreakdownRow
                label="pRBC"
                value={String(stats.wooPrbcCount ?? 0)}
                current={stats.wooPrbcCount ?? 0}
                previous={stats.prevWooPrbcCount}
              />
              <BreakdownRow
                label="FFP"
                value={String(
                  (stats.wooFfpSglCount ?? 0) + (stats.wooFfpDblCount ?? 0)
                )}
                current={
                  (stats.wooFfpSglCount ?? 0) + (stats.wooFfpDblCount ?? 0)
                }
                previous={stats.prevWooFfpCount}
              />
              <BreakdownRow
                label="SGL"
                value={String(stats.wooFfpSglCount ?? 0)}
                indent
              />
              <BreakdownRow
                label="DBL"
                value={String(stats.wooFfpDblCount ?? 0)}
                indent
              />
              {(stats.wooOtherCount ?? 0) > 0 && (
                <BreakdownRow
                  label="Other"
                  value={String(stats.wooOtherCount)}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <OpsMetricDetailModal
        visible={!!modalConfig}
        onClose={() => setModalConfig(null)}
        config={modalConfig}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default OpsOverviewTab;
